import React from 'react';
import { ArrowRight, ArrowLeft, Camera, Sparkles, Monitor, CheckCircle2 } from 'lucide-react';
import PublicHeader from './PublicHeader';
import './landing/luxe/luxe.css';

const FEATURES = [
  {
    icon: Camera,
    title: 'One photo, many looks',
    desc: 'Capture once and test multiple silhouettes, fabrics, and colors on the same person.',
  },
  {
    icon: Monitor,
    title: 'Real products, real context',
    desc: 'Grounded in your actual SKUs so try-ons map cleanly to what you can ship.',
  },
  {
    icon: Sparkles,
    title: 'AI-powered styling',
    desc: 'Refine outfits, angles, and lighting without repeating the entire shoot.',
  },
  {
    icon: CheckCircle2,
    title: 'Faster buying confidence',
    desc: 'Enable remote buyers to commit with fewer doubts and fewer returns.',
  },
];

const VirtualTryInfoPage = ({ onBack, onOpenVirtualTry, isAuthenticated, setActiveView, onSupportClick }) => {
  return (
    <div className="luxe-root min-h-screen">
      <PublicHeader
        activeViewId="virtualtry-info"
        setActiveView={setActiveView}
        variant="dark"
        onSupportClick={onSupportClick}
      />

      <section className="relative pt-24 sm:pt-28 pb-20 px-4 sm:px-6 luxe-bg-gradient luxe-grain min-h-screen">
        <div className="luxe-aurora" />
        <div className="relative container mx-auto max-w-6xl">
          <div className="flex items-center justify-between gap-3 mb-10">
            <button type="button" onClick={onBack} className="luxe-btn-ghost !px-4 !py-2 text-sm">
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <div className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/5 backdrop-blur px-4 py-2">
              <Camera className="w-4 h-4 text-gold" />
              <span className="text-sm font-semibold text-white/85">Virtual Try-On</span>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="relative order-1 lg:order-2">
              <div className="absolute -inset-6 bg-gradient-to-r from-gold/20 to-gold-deep/10 blur-3xl" />
              <div className="relative luxe-glass overflow-hidden">
                <img
                  src="/vton.gif"
                  alt="Virtual Try-On preview"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
                <div className="p-5 border-t border-white/10">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-white">Virtual Try-On</p>
                    <p className="text-xs text-gold/80 uppercase tracking-[0.2em]">Preview</p>
                  </div>
                  <p className="text-sm text-white/50 mt-2 font-light">
                    Capture a photo once, test multiple looks on real products, and share options your customers can react to instantly.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6 order-2 lg:order-1">
              <p className="luxe-eyebrow">See it before you stitch it</p>

              <h1 className="luxe-display text-4xl sm:text-5xl text-white">
                Let customers try your collection on,{' '}
                <span className="luxe-gold-text">virtually</span>.
              </h1>

              <p className="text-base sm:text-lg text-white/55 leading-relaxed max-w-xl font-light">
                Upload a photo, pick products, and watch AI generate try-on visuals that feel close to the fitting room—without
                the studio time or sample costs.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {FEATURES.map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="luxe-glass p-4 hover:luxe-hairline-gold transition-colors duration-500">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl border border-gold/30 bg-gold/10 flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-gold" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-white">{title}</h3>
                        <p className="text-sm text-white/50 mt-1 font-light">{desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                <button type="button" onClick={onOpenVirtualTry} className="luxe-btn-gold group">
                  {isAuthenticated ? 'Open Virtual Try-On' : 'Open Virtual Try-On (Login required)'}
                  <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-0.5 transition-transform" />
                </button>
                <button type="button" onClick={onBack} className="luxe-btn-ghost">
                  Explore Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VirtualTryInfoPage;
